import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {CaretDown, Sliders} from '@phosphor-icons/react';

import {getFontSize, getBorderRadius} from '../utils';
import GlobalControl from '../internal_components/_GlobalControl.react';
import {getTitleDescriptionId} from '../util/componentProps';

/**
 * A layout component for displaying a collapsible accordion item that can be
 * used to group and organize content.
 *
 * WARNING: This is an alpha version of a component. Breaking changes may be
 * made without respecting semantic versioning. Use at your own risk.
 *
 * **Example Usage**
 * ```
 * ddk._AccordionItem(
 *     title="Model Parameters",
 *     children=[
 *         dcc.Slider(name="n", min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5),
 *     ],
 * )
 * ```
 */
function _AccordionItem(props) {
    const {
        id,
        title,
        description,
        collapsed,
        children,
        margin,
        padding,
        radius,
        background_color,
        border_width,
        border_style,
        border_color,
        box_shadow,
        font_color,
        font_family,
        font_size,
    } = props;

    /* merge conditional styles from props */
    const style = {
        padding: padding,
        margin: margin,
        backgroundColor: background_color,
        borderWidth: border_width,
        borderStyle: border_style,
        borderColor: border_color,
        borderRadius: getBorderRadius(radius),
        boxShadow: box_shadow,
        color: font_color,
        fontFamily: font_family,
        fontSize: getFontSize(font_size),
        ...(props.style ?? {}),
    };

    const [isCollapsed, setIsCollapsed] = useState(collapsed);

    const toggleCollapsed = () => {
        setIsCollapsed(prevState => !prevState);
    };

    return (
        <div
            className={`layout-accordion-container ${props.className ?? ''}`}
            id={id}
            style={style}
        >
            <div className="accordion-header" onClick={toggleCollapsed}>
                <CaretDown
                    className={`accordion-toggle-button ${
                        isCollapsed ? 'collapsed' : ''
                    }`}
                    size={24}
                />
                {title && <div className="accordion-title">{title}</div>}
                <div className="accordion-tag">
                    <Sliders size={16} />
                    {children.length}
                </div>
            </div>
            <div
                className={`accordion-slide-wrapper ${
                    isCollapsed ? 'collapsed' : ''
                }`}
            >
                <div className="accordion-content-wrapper">
                    <div className="accordion-content">
                        {description && (
                            <div className="accordion-description">
                                {description}
                            </div>
                        )}
                        {children.map(child => {
                            const {title, description, id} =
                                getTitleDescriptionId(child);

                            return (
                                <GlobalControl
                                    key={id}
                                    label={title}
                                    tooltip={description}
                                >
                                    {child}
                                </GlobalControl>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

PropTypes.defaultProps = {
    collapsed: false,
};

_AccordionItem.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * Optional user-defined CSS class for the AccordionItem
     */
    className: PropTypes.string,

    /**
     * An optional prop to pass any additional children to the AccordionItem.
     */
    children: PropTypes.node,

    /**
     * An optional prop to set the initial opened or closed state of the AccordionItem.
     */
    collapsed: PropTypes.bool,

    /**
     * The content, which may include Markdown formatting, to display in the AccordionItem.
     */
    text: PropTypes.string,

    /**
     * Prop to set the title or heading displayed on the AccordionItem.
     */
    title: PropTypes.string,

    /**
     * Prop to set the description displayed on the AccordionItem.
     */
    description: PropTypes.string,

    /**
     * An optional icon to display on the accordion, which can be either a string
     * representing a Font Awesome icon name (without the "fa-"
     * prefix) or an object specifying the icon name and category.
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            /**
             * The name of the Font Awesome icon without the "fa-" prefix.
             */
            icon_name: PropTypes.string,

            /**
             * The Font Awesome category for the icon, which must be one of the following:
             * - "solid"
             * - "regular"
             * - "brands"
             * - "light"
             */
            icon_category: PropTypes.oneOf([
                // PropTypes have to be inline in js, unhandled imports by react-docgen.
                'solid',
                'regular',
                'brands',
                'light',
            ]),
        }),
    ]),

    /**
     * The border radius for the AccordionItem: "sm" (0.125rem), "md" (0.25rem), "lg" (0.75rem), or "none".
     */
    radius: PropTypes.oneOf(['sm', 'md', 'lg', 'none']),

    /**
     * Space (in pixels) surrounding the AccordionItem.
     * Overrides theme.accordion_margin.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the AccordionItem, between the border
     * and the edge of the content.
     * Overrides theme.accordion_padding.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the AccordionItem. Overrides theme.AccordionItem_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the AccordionItem. Overrides theme.accordion_box_shadow.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the AccordionItem. Overrides theme.accordion_border.width.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the AccordionItem. Overrides theme.accordion_border.style.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the AccordionItem. Overrides theme.accordion_border.color.
     */
    border_color: PropTypes.string,

    /**
     * The font color of AccordionItem children. Overrides theme.accordion_text
     */
    font_color: PropTypes.string,

    /**
     * The font family of AccordionItem children. Overrides theme.font_family_AccordionItemItem
     */
    font_family: PropTypes.string,

    /**
     * The font size of AccordionItem children.
     * Can be a relative size, "smaller", "normal", "larger",
     * or a CSS attribute size (e.g. `px`, 'em`)
     */
    font_size: PropTypes.string,

    /**
     * The style object of the outermost div of the AccordionItem,
     * with values provided as strings and keys in camelCase.
     * Style properties in pixel units can be supplied as just numbers
     * without the px unit.
     *
     * For example:
     *
     * ```
     * {
     *   "fontSize": "16px",
     *   "borderRadius: "4",
     * }
     * ```
     */
    style: PropTypes.object,
};

export default _AccordionItem;
